
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import { sourcesConfig } from '@/hooks/useNewsFeed';

interface NewsSourceFilterProps {
  sources: string[];
  onToggleSource: (source: string) => void;
}

export function NewsSourceFilter({
  sources,
  onToggleSource
}: NewsSourceFilterProps) {
  const { t, language } = useLanguage();
  let availableSources = sourcesConfig[language as keyof typeof sourcesConfig] || [];

  // If Polish, include English sources
  if (language === 'pl') {
    availableSources = [...availableSources, ...sourcesConfig.en];
  }

  const areAllSelected = availableSources.length > 0 && sources.length === availableSources.length;

  // Determine accent color based on source hash (simple deterministic color)
  const getAccentColor = (source: string) => {
    if (!source) return 'border-gray-500 text-gray-500';
    const colors = [
      'border-red-500 text-red-500',
      'border-blue-500 text-blue-500',
      'border-green-500 text-green-500',
      'border-purple-500 text-purple-500',
      'border-orange-500 text-orange-500',
      'border-cyan-500 text-cyan-500'
    ];
    const hash = source.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">{t('news.sources')}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleSource('ALL')}
          className="text-xs text-muted-foreground hover:text-primary"
        >
          {areAllSelected ? t('common.deselect_all') || 'Deselect All' : t('common.select_all') || 'Select All'}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {availableSources.map((source) => (
          <Button
            key={source}
            variant="outline"
            size="sm"
            className={cn(
              "rounded-full border transition-all duration-300",
              sources.includes(source)
                ? cn("bg-primary/10", getAccentColor(source))
                : "bg-transparent border-muted-foreground/50 text-muted-foreground hover:border-primary"
            )}
            onClick={() => onToggleSource(source)}
          >
            {sources.includes(source) ? (
              <Check className="h-3.5 w-3.5 mr-1" />
            ) : (
              <X className="h-3.5 w-3.5 mr-1" />
            )}
            {source}
          </Button>
        ))}
      </div>
    </div>
  );
}
